import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { TenantLogo } from '@/components/shell/TenantLogo';

import { useAuth } from './useAuth';

/**
 * Branded dead-end for an authenticated-but-not-ACTIVE account (PTV2-1424,
 * DESIGN §9.2). Generalises the PTV2-1413 `NoPermissionsPage` pattern: a
 * non-ACTIVE user is blocked at authentication (the backend
 * `AccountStatusFilter` refuses every non-`/me` path with a 403 `account_*`
 * code), so `SessionGuard` renders one of these instead of the app shell. The
 * only request in flight is the `/me` probe, and the only affordance is
 * sign-out — there is nothing else this user can do here.
 */
function AccountStatePage({
  heading,
  message,
}: {
  readonly heading: string;
  readonly message: string;
}): ReactElement {
  const { t } = useTranslation();
  const { signOut } = useAuth();

  return (
    <main
      role="alert"
      className="flex min-h-screen items-center justify-center bg-muted/30 p-6"
    >
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center">
          <div className="mb-4 flex items-center gap-2.5">
            <TenantLogo />
            <span className="text-lg font-semibold tracking-tight text-brand">
              {t('shell.rail.brandName')}
            </span>
          </div>
          <CardTitle>{heading}</CardTitle>
          <CardDescription>{message}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => {
              void signOut();
            }}
          >
            {t('auth.signOut')}
          </Button>
        </CardContent>
      </Card>
    </main>
  );
}

/** `status === 'PENDING_ACTIVATION'` — a JIT-provisioned account awaiting verification. */
export function AccountPendingPage(): ReactElement {
  const { t } = useTranslation();
  return (
    <AccountStatePage
      heading={t('auth.accountPending.heading')}
      message={t('auth.accountPending.message')}
    />
  );
}

/** `status === 'DEACTIVATED'` — an account deactivated after use. */
export function AccountDeactivatedPage(): ReactElement {
  const { t } = useTranslation();
  return (
    <AccountStatePage
      heading={t('auth.accountDeactivated.heading')}
      message={t('auth.accountDeactivated.message')}
    />
  );
}
